import axios from "axios";
import { ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { COUNTRY_PHONE_CODES_LIST } from "base/constants";
import EventTracker, { FREE_TRIAL_EVENTS } from "base/eventsTracker";
import { GenericException } from "base/ui/errors";
import { setKeyPath } from "base/utils";
import { doClickOnEnter } from "base/utils/common";

import { DEFAULT_FEATURES, INDUSTRIES } from "./ContactUsForm";
import { LoadingOverlay } from "base/ui/status";
import { SukhibaSideFrame } from "../../components/ui/commonUI";

const exclude_emails = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "rediffmail.com", "protonmail.com", "icloud.com",
	"aol.com", "yandex.com", "mail.com", "live.com", "msn.com", "gmx.com", "tutanota.com", "email.com",];


function FreeTrialForm({is_full_page}) {

	const [login_data, setLoginData] = useState();
	const [errors, setErrors] = useState({});
	const [is_loading, setIsLoading] = useState(false);
	const [form_data, setFormData] = useState({}); // phone_number, country_phone_code, otp, name, email, industry, features
	const [selected_industry, setSelectedIndustry] = useState();
	const [search_params] = useSearchParams();

	useEffect(() => {
		axios.get("/user/location").then((resp) => {
			setFormData({...form_data, "country_phone_code": `+${resp.data.phone_code}`});
		});
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		// if name is email check to allow only business/company emails
		setFormData({...form_data, [name]: value });
		if (name === "email_id" && exclude_emails.some((domain) => value.endsWith(`@${domain}`))) {
			setErrors({ ...errors, "email_id": "Please enter a valid business email" });
			return
		}
		setErrors({})
	};

	const registerOnHubspot = (ctx) => {
		const payload = {
			"fields": [
				{
					"objectTypeId": "0-1",
					"name": "email",
					"value": ctx.email_id
				},
				{
					"objectTypeId": "0-1",
					"name": "firstname",
					"value": ctx.name
				},
				{
					"objectTypeId": "0-1",
					"name": "company",
					"value": ctx.company_name
				},
				{
					"objectTypeId": "0-1",
					"name": "hs_whatsapp_phone_number",
					"value": ctx.country_phone_code + ctx.phone_number
				},
				{
					"objectTypeId": "0-1",
					"name": "industry",
					"value": ctx.industry
				},
				{
					"objectTypeId": "0-1",
					"name": "pricing_preferences",
					"value": "Free Trial"
				},
			],
			"context": {
				"pageUri": window.location.href,
				"pageName": "Home"
			}
		}
		axios.post(`/company/contact`, payload)
	}



	const handleSubmit = (e) => {
		e.preventDefault();
		if (is_loading) return;
		if (errors && Object.keys(errors).length > 0) return;

		setIsLoading(true);
		// utm search_params
		const utm_params = {};
		for (const [key, value] of search_params.entries()) {
			if (key.startsWith("utm")) {
				utm_params[key] = value;
			}
		}
		const selected_features = DEFAULT_FEATURES.filter((feature) => feature.checked);
		const features = selected_features.map((feature) => feature.name);
		const payload = {
			...form_data,
			industry: selected_industry === "Other(please specify)" ? form_data.industry : selected_industry,
			features: features,
			utm_params: utm_params
		}
		if (!payload.otp) {
			EventTracker.track(FREE_TRIAL_EVENTS.SIGN_UP_OTP_REQUESTED, payload)
		}
		axios.post(`/api/start_free_trial`, payload).then((resp) => {
			if (resp.data.errors) {
				setErrors(resp.data.errors);
				return;
			}
			if (resp.data.redirect) {
				EventTracker.track(FREE_TRIAL_EVENTS.SIGN_UP_SUCCESS)
				registerOnHubspot(payload)
				window.location.href = `${resp.data.redirect}?tab=Overview`;
			}
			setLoginData(resp.data);
		}).catch((err) => {
			setErrors(err)
		}).finally(() => {
			setIsLoading(false);
		});
	};

	const handleGoBack = () => {
		setLoginData(null);
		setFormData({...form_data, otp: null});
	};

	return (
		<SukhibaSideFrame is_full_page={is_full_page}> 
			{errors
				? <div className="md:tw-absolute tw-right-4 tw-top-4"><GenericException ex={errors} /></div>
				: null
			}
			{
				is_loading ? <LoadingOverlay /> : null
			}
			{!login_data?.otp_sent
			? (
				<div className="tw-mt-3 tw-mb-5 md:tw-p-4">
					<div className="tw-text-xl !tw-font-bold font-inter">Create your account</div>
					{/* <div className="tw-text-xs tw-text-[#212B36] tw-font-normal">
							Experience 360° WhatsApp Business Platform right away with our FREE plan.
						</div> */}
					<form onSubmit={handleSubmit} className="tw-mt-5">
						<div className="tw-mb-4">
							<p className="tw-text-[11px] md:tw-text-[14px] tw-mb-1 tw-text-[#666]">
								Organization Name<span className="tw-text-red-500 tw-ml-1">*</span>
							</p>
							<input
								id="company_name"
								className="input-primary"
								placeholder="Enter Organization Name"
								name="company_name"
								value={form_data.company_name || ""}
								onChange={handleChange}
								autoComplete="off"
							/>
						</div>
						<div className="tw-mb-4">
							<p className="tw-text-[11px] md:tw-text-[14px] tw-mb-1 tw-text-[#666]">
								Business Email<span className="tw-text-red-500 tw-ml-1">*</span>
							</p>
							<input
								id="email"
								className="input-primary"
								placeholder="Enter Business Email"
								name="email_id"
								value={form_data.email_id || ""}
								onChange={handleChange}
								autoComplete="off"
								type="email"
							/>
						</div>
						<div className="tw-mb-4">
							<p className="tw-text-[11px] md:tw-text-[14px] tw-mb-1 tw-text-[#666]">
								Your Name<span className="tw-text-red-500 tw-ml-1">*</span>
							</p>
							<input
								className="input-primary"
								placeholder="Enter Name"
								id="name"
								name="name"
								value={form_data.name || ""}
								onChange={handleChange}
								autoComplete="off"
							/>
						</div>
						<div className="tw-mb-4">
							<p className="tw-text-[11px] md:tw-text-[14px] tw-mb-1 tw-text-[#666]">
								WhatsApp Number<span className="tw-text-red-500 tw-ml-1">*</span>
							</p>
							<div className="tw-flex tw-border tw-rounded tw-text-sm tw-w-full">
								<select
									className="tw-outline-none tw-text-xs tw-py-2 tw-mr-1 tw-w-[136px]"
									value={form_data.country_phone_code || ""}
									name="country_phone_code"
									onChange={handleChange}
								>
									{COUNTRY_PHONE_CODES_LIST.map((obj) => (
										<option key={obj.code} value={obj.dial_code}>
											{obj.flag} {obj.name} {obj.dial_code}
										</option>
									))}
								</select>
								<input
									className="input-primary tw-border-none tw-outline-none tw-text-md tw-p-2 tw-mr-1 tw-w-full"
									placeholder="Enter WhatsApp Number"
									id="phone_number"
									name="phone_number"
									defaultValue={form_data.phone_number || ""}
									onChange={handleChange}
									autoComplete="off"
									type="number"
								/>
							</div>
						</div>
						<div className="tw-mb-4">
							<p className="tw-text-[11px] md:tw-text-[14px] tw-mb-1 tw-text-[#666]">
								Industry<span className="tw-text-red-500 tw-ml-1">*</span>
							</p>
							<div className="tw-flex tw-flex-wrap tw-gap-2 md:tw-gap-4 tw-flex-col">
								<select
									className="twx-dropdown max-md:tw-w-full md:tw-flex-1"
									defaultValue=""
									onChange={(e) => {
										setSelectedIndustry(e.target.value);
									}}
								>
									<option value="" disabled={true}>
										Select Industry
									</option>
									{INDUSTRIES.map((industry) => {
										return (
											<option key={industry} value={industry}>
												{industry}
											</option>
										);
									})}
								</select>
								{selected_industry === "Other(please specify)" ? (
									<div className="max-lg:tw-w-full tw-flex-1">
										<input
											className="input-primary tw-border tw-px-4 tw-rounded tw-text-sm tw-py-2 tw-w-full"
											placeholder="Enter Industry"
											id="industry"
											name="industry"
											onChange={handleChange}
											autoComplete="off"
										/>
									</div>
								) : null}
							</div>
						</div>
						<div className="tw-mb-4">
							<p className="tw-text-[11px] md:tw-text-[14px] tw-mb-1 tw-text-[#666]">
								What Flowcart features are you interested in?<span className="tw-text-secondary-text tw-ml-1 tw-text-[11px]">(Optional)</span>
							</p>
							<div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-2">
								{DEFAULT_FEATURES.map((feature) => {
									return (
										<label key={feature.id} htmlFor={feature.id} className="tw-flex tw-flex-wrap tw-gap-1 tw-items-center">
											<input
												className="input-primary"
												type="checkbox"
												onChange={(e) => {
													setKeyPath(DEFAULT_FEATURES, `${feature.id}.checked`, e.target.checked);
												}}
											/>
											<span className="tw-text-sm tw-text-gray-600">{feature.name}</span>
										</label>
									);
								})}
							</div>
						</div>
						<button
							className={`btn-primary-v2 tw-px-[22px] tw-py-[11px] tw-text-[16px] tw-font-bold tw-leading-[19px] tw-w-full tw-mt-8 ${is_loading ? "tw-cursor-not-allowed" : "tw-cursor-pointer"}`}
							type="submit"
							disabled={is_loading}
						>
							Send OTP
						</button>
						<div className="tw-text-xs tw-text-gray-500 tw-mt-2 tw-float-right">
							Already have an account?{" "}
							<a href="/admin" className="tw-text-primary-dark tw-underline">
								Login
							</a>
						</div>
					</form>
				</div>
			) 
			: (
				<div className="md:tw-py-8">
					<div className="tw-mb-14 tw-cursor-pointer" onClick={handleGoBack} onKeyUp={doClickOnEnter}>
						<ArrowLeft className="tw--m-2" />
					</div>
					<div className="tw-text-xl !tw-font-bold font-inter">Verify OTP</div>
					<div className="tw-text-sm max-md:tw-text-xs tw-text-[#212B36] tw-font-normal">
						Enter the OTP sent on your{" "}
						<span className="tw-text-[#212B36] tw-font-semibold">
							Phone No:{form_data.country_phone_code} {form_data.phone_number}
						</span>
					</div>
					<form onSubmit={handleSubmit}>
						<div className="tw-w-full tw-mt-5">
							<input
								type="number"
								autoFocus={true}
								className="input-primary"
								placeholder="OTP"
								onChange={handleChange}
								id="otp"
								name="otp"
							/>
						</div>
						<button
							className={`btn-primary-v2 tw-px-[22px] tw-py-[11px] tw-text-[16px] tw-font-bold tw-leading-[19px] tw-w-full tw-mt-8 ${
								is_loading 
								? "tw-cursor-not-allowed" 
								: "tw-cursor-pointer"
								} tw-mb-5`
							}
							type="submit"
							disabled={is_loading}
						>
							SUBMIT
						</button>
					</form>
				</div>
			)}
		</SukhibaSideFrame>
	);
}


export default FreeTrialForm;
